"use client";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]):not([aria-disabled="true"]), [role="menuitem"]:not([aria-disabled="true"]), [href]:not([aria-disabled="true"]), input:not([disabled]):not([aria-disabled="true"]), select:not([disabled]):not([aria-disabled="true"]), textarea:not([disabled]):not([aria-disabled="true"]), [tabindex]:not([tabindex="-1"])';

import clsx from "clsx";
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from "react";
import {
  Children,
  Fragment,
  MouseEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PopoverProps } from "react-tiny-popover";
import { ButtonProps } from "./Button";
import PopoverPannel from "./PopoverPannel";

type DropdownMenuRenderProps = {
  paddingClassName: string;
};

type DropdownMenuContentRenderProps = DropdownMenuRenderProps & {
  close: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  focusFirstItem: () => void;
  focusLastItem: () => void;
  focusNextItem: () => void;
  focusPreviousItem: () => void;
};

type DropdownMenuContextValue = {
  open: boolean;
  close: () => void;
  setOpen: (open: boolean) => void;
  paddingClassName: string;
  focusFirstItem: () => void;
  focusLastItem: () => void;
  focusNextItem: () => void;
  focusPreviousItem: () => void;
};

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(
  null,
);

type OpenMenuRegistryEntry = {
  contentRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLElement | null>;
  close: () => void;
};

// Every open DropdownMenu registers here so that opening a menu closes all
// other open menus except its ancestors (menus whose panel hosts its
// trigger). Outside-click detection alone can't guarantee this: triggers that
// stopPropagation() (common inside menus) swallow the click before sibling
// popovers' outside-click listeners see it, leaving two cascades open at once.
const openMenuRegistry = new Set<OpenMenuRegistryEntry>();

// Nested DropdownMenus portal each panel to the body, so DOM containment is
// only one level deep: grandparent.content does not contain grandchild.trigger.
// Walk the open cascade via each menu's trigger to keep the full ancestor chain
// (e.g. Accounts → Cadence → Custom) instead of collapsing it.
const isAncestorMenu = (
  candidate: OpenMenuRegistryEntry,
  descendant: OpenMenuRegistryEntry,
): boolean => {
  let current: OpenMenuRegistryEntry | null = descendant;
  const visited = new Set<OpenMenuRegistryEntry>();
  while (current) {
    if (visited.has(current)) return false;
    visited.add(current);
    const trigger = current.triggerRef.current;
    if (!trigger) return false;
    let parent: OpenMenuRegistryEntry | null = null;
    for (const other of openMenuRegistry) {
      if (other === current) continue;
      if (other.contentRef.current?.contains(trigger)) {
        parent = other;
        break;
      }
    }
    if (!parent) return false;
    if (parent === candidate) return true;
    current = parent;
  }
  return false;
};

export const DROPDOWN_MENU_STYLE: Record<
  NonNullable<ButtonProps["size"]>,
  string
> = {
  xs: "p-1",
  s: "p-1",
  m: "p-1",
  l: "p-1",
};

type DropdownMenuRootProps = {
  children:
    | ReactNode
    | ((helpers: DropdownMenuContentRenderProps) => ReactNode);
  size?: ButtonProps["size"];
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
} & Omit<PopoverProps, "content" | "children" | "isOpen">;

type DropdownMenuTriggerProps = {
  children: ReactElement;
};

type DropdownMenuContentProps = {
  children:
    | ReactNode
    | ((helpers: DropdownMenuContentRenderProps) => ReactNode);
  className?: string;
  disablePadding?: boolean;
};

const DropdownMenuTrigger = (_props: DropdownMenuTriggerProps) => null;
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

const DropdownMenuContent = (_props: DropdownMenuContentProps) => null;
DropdownMenuContent.displayName = "DropdownMenuContent";

const DropdownMenuRoot = ({
  children,
  size = "m",
  open,
  defaultOpen = false,
  onOpenChange,
  onClickOutside,
  ...popoverProps
}: DropdownMenuRootProps) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [isUncontrolledOpen, setIsUncontrolledOpen] = useState(defaultOpen);
  const isControlled = typeof open === "boolean";
  const currentOpen = isControlled ? (open as boolean) : isUncontrolledOpen;

  const emitOpenChange = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setIsUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const [focusIntent, setFocusIntent] = useState<"first" | "last" | null>(null);

  // Whether the current pointer press began inside our content. Outside-click
  // detection runs on `click`, whose target is the common ancestor of the
  // mousedown and mouseup targets — so when a mousedown inside the content
  // shrinks it (e.g. blurring an expanded field collapses it and, for a
  // popover anchored above its trigger, drops the top edge), the mouseup can
  // land past the new edge and the click resolves to <body>, reading as an
  // outside click. A press that starts inside must never close the menu.
  const pressStartedInsideContentRef = useRef(false);

  useEffect(() => {
    if (!currentOpen || typeof document === "undefined") return;
    const handlePointerDown = (event: PointerEvent) => {
      pressStartedInsideContentRef.current =
        event.target instanceof Node &&
        contentRef.current?.contains(event.target) === true;
    };
    // Reset after react-tiny-popover's body-level click handler has consumed
    // the flag (bubble order reaches body before document), so a stale press
    // can't suppress a later keyboard-synthesized click (which has no
    // preceding pointerdown).
    const handleClickReset = () => {
      pressStartedInsideContentRef.current = false;
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("click", handleClickReset);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("click", handleClickReset);
    };
  }, [currentOpen]);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!next) {
        setFocusIntent(null);
      }
      emitOpenChange(next);
    },
    [emitOpenChange],
  );

  const close = useCallback(() => setOpen(false), [setOpen]);
  const toggle = useCallback(
    () => setOpen(!currentOpen),
    [currentOpen, setOpen],
  );

  // Single-open-branch rule: while open, this menu lives in the registry;
  // when it opens it closes every registered menu that isn't one of its
  // ancestors, so at most one cascade branch is open at a time app-wide.
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  }, [close]);
  const registryEntryRef = useRef<OpenMenuRegistryEntry | null>(null);
  useEffect(() => {
    if (!currentOpen) return;
    if (!registryEntryRef.current) {
      registryEntryRef.current = {
        contentRef,
        triggerRef,
        close: () => closeRef.current(),
      };
    }
    const entry = registryEntryRef.current;
    // triggerRef is only captured on click/keydown (react-tiny-popover
    // overwrites the ref we clone onto the trigger), so a programmatic open
    // can reach here with no trigger node. Without it ancestry can't be
    // resolved, so leave other menus alone rather than closing an ancestor.
    if (entry.triggerRef.current) {
      // Register before closing siblings so transitive ancestry can walk
      // through this entry when a deeper nest opens (e.g. Custom under Cadence).
      openMenuRegistry.add(entry);
      for (const other of Array.from(openMenuRegistry)) {
        if (other === entry) continue;
        if (isAncestorMenu(other, entry)) continue;
        other.close();
      }
    } else {
      openMenuRegistry.add(entry);
    }
    return () => {
      openMenuRegistry.delete(entry);
    };
  }, [currentOpen]);

  const paddingClassName = DROPDOWN_MENU_STYLE[size ?? "m"];

  const runNextFrame = useCallback((fn: () => void) => {
    if (typeof window === "undefined") {
      fn();
      return;
    }
    window.requestAnimationFrame(fn);
  }, []);

  const isElementFocusable = useCallback((element: HTMLElement | null) => {
    if (!element) return false;
    const ariaDisabled = element.getAttribute("aria-disabled");
    const tabIndex = element.getAttribute("tabindex");
    const isDisabled =
      ariaDisabled === "true" ||
      tabIndex === "-1" ||
      element.hasAttribute("disabled");
    const hasVisibleRect =
      typeof element.getClientRects === "function" &&
      element.getClientRects().length > 0;
    return !isDisabled && hasVisibleRect;
  }, []);

  const getFocusableItems = useCallback(() => {
    const container = contentRef.current;
    if (!container) return [] as HTMLElement[];
    const items = Array.from<HTMLElement>(
      container.querySelectorAll(FOCUSABLE_SELECTOR),
    ).filter((element) => isElementFocusable(element));
    return items;
  }, [isElementFocusable]);

  const findFocusableAncestor = useCallback(
    (node: EventTarget | null) => {
      if (!(node instanceof HTMLElement)) return null;
      const container = contentRef.current;
      if (!container) return null;
      const candidate = node.closest(FOCUSABLE_SELECTOR) as HTMLElement | null;
      if (!candidate || !container.contains(candidate)) return null;
      return isElementFocusable(candidate) ? candidate : null;
    },
    [isElementFocusable],
  );

  const focusFirstItem = useCallback(() => {
    const [first] = getFocusableItems();
    first?.focus();
  }, [getFocusableItems]);

  const focusLastItem = useCallback(() => {
    const items = getFocusableItems();
    if (!items.length) return;
    items[items.length - 1]?.focus();
  }, [getFocusableItems]);

  const focusNextItem = useCallback(() => {
    if (typeof document === "undefined") return;
    const items = getFocusableItems();
    if (!items.length) return;
    const activeElement = document.activeElement as HTMLElement | null;
    const currentIndex = activeElement ? items.indexOf(activeElement) : -1;
    const nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + 1) % items.length;
    items[nextIndex]?.focus();
  }, [getFocusableItems]);

  const focusPreviousItem = useCallback(() => {
    if (typeof document === "undefined") return;
    const items = getFocusableItems();
    if (!items.length) return;
    const activeElement = document.activeElement as HTMLElement | null;
    const currentIndex = activeElement ? items.indexOf(activeElement) : -1;
    const prevIndex =
      currentIndex === -1
        ? items.length - 1
        : (currentIndex - 1 + items.length) % items.length;
    items[prevIndex]?.focus();
  }, [getFocusableItems]);

  useEffect(() => {
    if (!currentOpen) {
      if (focusIntent !== null) {
        setFocusIntent(null);
      }
      return;
    }
    if (!focusIntent) return;

    runNextFrame(() => {
      if (focusIntent === "first") {
        focusFirstItem();
      } else {
        focusLastItem();
      }
    });
    setFocusIntent(null);
  }, [currentOpen, focusIntent, focusFirstItem, focusLastItem, runNextFrame]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const focusable = findFocusableAncestor(event.target);
      if (!focusable || document.activeElement === focusable) return;
      focusable.focus({ preventScroll: true });
    },
    [findFocusableAncestor],
  );

  const handleContentKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          event.stopPropagation();
          focusNextItem();
          break;
        case "ArrowUp":
          event.preventDefault();
          event.stopPropagation();
          focusPreviousItem();
          break;
        case "Home":
          event.preventDefault();
          event.stopPropagation();
          focusFirstItem();
          break;
        case "End":
          event.preventDefault();
          event.stopPropagation();
          focusLastItem();
          break;
        case "Tab":
          close();
          break;
        case "Escape":
          event.preventDefault();
          event.stopPropagation();
          close();
          runNextFrame(() => {
            triggerRef.current?.focus();
          });
          break;
        default:
          break;
      }
    },
    [
      close,
      focusFirstItem,
      focusLastItem,
      focusNextItem,
      focusPreviousItem,
      runNextFrame,
    ],
  );

  const contextValue = useMemo(
    () => ({
      open: currentOpen,
      close,
      setOpen,
      paddingClassName,
      focusFirstItem,
      focusLastItem,
      focusNextItem,
      focusPreviousItem,
    }),
    [
      close,
      currentOpen,
      focusFirstItem,
      focusLastItem,
      focusNextItem,
      focusPreviousItem,
      paddingClassName,
      setOpen,
    ],
  );

  const renderHelpers: DropdownMenuContentRenderProps = {
    paddingClassName,
    close,
    open: currentOpen,
    setOpen,
    focusFirstItem,
    focusLastItem,
    focusNextItem,
    focusPreviousItem,
  };

  const resolvedChildren =
    typeof children === "function" ? children(renderHelpers) : children;

  const childrenToMap =
    isValidElement(resolvedChildren) && resolvedChildren.type === Fragment
      ? (resolvedChildren.props as { children?: ReactNode }).children
      : resolvedChildren;

  const childArray = Children.toArray(childrenToMap);

  const triggerChild = childArray.find(
    (child): child is ReactElement<DropdownMenuTriggerProps> =>
      isValidElement(child) && child.type === DropdownMenuTrigger,
  );
  const contentChild = childArray.find(
    (child): child is ReactElement<DropdownMenuContentProps> =>
      isValidElement(child) && child.type === DropdownMenuContent,
  );

  if (!triggerChild || !contentChild) {
    if (process.env.NODE_ENV !== "production") {
      throw new Error(
        "DropdownMenu requires both <DropdownMenu.Trigger> and <DropdownMenu.Content> children.",
      );
    }
    return null;
  }

  type TriggerElement = ReactElement<Record<string, any>>;
  const triggerElement = Children.only(
    triggerChild.props.children,
  ) as TriggerElement;
  if (!isValidElement(triggerElement)) {
    throw new Error(
      "<DropdownMenu.Trigger> expects a single React element child.",
    );
  }

  const trigger = cloneElement(triggerElement, {
    "aria-haspopup": "menu",
    "aria-expanded": currentOpen,
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      // React 19 exposes the element's ref via props.ref; older React via element.ref.
      const originalRef =
        (triggerElement.props as any).ref ?? (triggerElement as any).ref;
      if (typeof originalRef === "function") {
        originalRef(node);
      } else if (originalRef && typeof originalRef === "object") {
        (originalRef as { current: HTMLElement | null }).current = node;
      }
    },
    onClick: (event: MouseEvent) => {
      // react-tiny-popover re-clones the trigger and overwrites the ref above,
      // so capture the trigger node from the event as a reliable fallback.
      triggerRef.current = event.currentTarget as HTMLElement;
      triggerElement.props.onClick?.(event);
      if (event.defaultPrevented) return;
      toggle();
    },
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
      triggerRef.current = event.currentTarget;
      triggerElement.props.onKeyDown?.(event);
      if (event.defaultPrevented) return;
      switch (event.key) {
        case " ":
        case "Enter":
          event.preventDefault();
          if (currentOpen) {
            close();
          } else {
            setFocusIntent("first");
            setOpen(true);
          }
          break;
        case "ArrowDown":
          event.preventDefault();
          if (!currentOpen) {
            setFocusIntent("first");
            setOpen(true);
          } else {
            focusFirstItem();
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (!currentOpen) {
            setFocusIntent("last");
            setOpen(true);
          } else {
            focusLastItem();
          }
          break;
        case "Escape":
          if (!currentOpen) break;
          event.preventDefault();
          event.stopPropagation();
          close();
          break;
        default:
          break;
      }
    },
  });

  const resolvedContent = currentOpen
    ? (() => {
        const contentBody =
          typeof contentChild.props.children === "function"
            ? contentChild.props.children(renderHelpers)
            : contentChild.props.children;

        return (
          <DropdownMenuContext.Provider value={contextValue}>
            <div
              ref={contentRef}
              role="menu"
              aria-orientation="vertical"
              tabIndex={-1}
              onKeyDown={handleContentKeyDown}
              onMouseMove={handleMouseMove}
              // Menu container is focusable for keyboard nav, but never show
              // a focus ring — indication lives on items / fields inside.
              className={clsx(
                "outline-none",
                !contentChild.props.disablePadding && paddingClassName,
                contentChild.props.className
              )}
            >
              {contentBody}
            </div>
          </DropdownMenuContext.Provider>
        );
      })()
    : null;

  return (
    <PopoverPannel
      {...popoverProps}
      size={size}
      content={resolvedContent}
      isOpen={currentOpen}
      onClickOutside={(event) => {
        // A press that began inside the content is ours even if the click
        // resolved outside (see pressStartedInsideContentRef) — also covers
        // select-text-and-drag-out gestures.
        if (pressStartedInsideContentRef.current) return;
        onClickOutside?.(event);
        if (event.defaultPrevented) return;
        // Clicks landing in another popover panel: if that panel hosts our
        // trigger (we're nested in it), the user clicked a sibling control, so
        // close. Otherwise it's a portal spawned from our own content (nested
        // dropdown, hover popover) and we must stay open.
        // The panel is resolved from composedPath() rather than live DOM
        // ancestry: for trusted clicks React may unmount the clicked panel
        // (e.g. a nested select closing on option click) before this body
        // listener runs, and the path snapshot still holds the original chain.
        const target = event.target as HTMLElement | null;
        const path =
          typeof event.composedPath === "function" ? event.composedPath() : [];
        const panel =
          (path.find(
            (node): node is HTMLElement =>
              node instanceof HTMLElement &&
              node.classList.contains("popover-menu"),
          ) ??
            target?.closest?.(".popover-menu")) ||
          null;
        if (panel && !panel.contains(triggerRef.current)) return;
        close();
      }}
    >
      {trigger}
    </PopoverPannel>
  );
};

const DropdownMenu = Object.assign(DropdownMenuRoot, {
  Trigger: DropdownMenuTrigger,
  Content: DropdownMenuContent,
});

export default DropdownMenu;
